import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const WebSocketContext = createContext(null);

const STORAGE_KEY  = 'rvn_live_logs';
const MAX_AGE_MS   = 8 * 60 * 60 * 1000; // 8 giờ
const MAX_LOGS     = 500;

const loadStored = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const logs = JSON.parse(raw);
        const cutoff = Date.now() - MAX_AGE_MS;
        return logs.filter(l => {
            const ts = l.timestamp || l.createdAt;
            return ts ? new Date(ts).getTime() > cutoff : true;
        });
    } catch { return []; }
};

const saveToStorage = (logs) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
    } catch {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(logs.slice(0, 100))); } catch {}
    }
};

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) throw new Error('useWebSocket must be used within WebSocketProvider');
    return context;
};

export const WebSocketProvider = ({ children }) => {
    const [connected, setConnected]       = useState(false);
    const [realtimeLogs, setRealtimeLogs] = useState(() => loadStored());
    const [newLogCount, setNewLogCount]   = useState(0);
    const wsRef           = useRef(null);
    const reconnectTimer  = useRef(null);
    const listeners       = useRef({});

    // Persist to localStorage whenever logs change
    useEffect(() => {
        saveToStorage(realtimeLogs);
    }, [realtimeLogs]);

    const connect = useCallback(() => {
        // /api/ws — webpack-dev-server proxy /api/* tới Express, tránh đụng /ws của HMR
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const WS_URL = process.env.REACT_APP_WS_URL || `${proto}//${window.location.host}/api/ws`;
        try {
            const ws = new WebSocket(WS_URL);
            wsRef.current = ws;

            ws.onopen = () => {
                setConnected(true);
                if (reconnectTimer.current) {
                    clearTimeout(reconnectTimer.current);
                    reconnectTimer.current = null;
                }
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    handleMessage(data);
                } catch (e) {}
            };

            ws.onclose = () => {
                setConnected(false);
                reconnectTimer.current = setTimeout(connect, 3000);
            };

            ws.onerror = () => { ws.close(); };
        } catch {
            reconnectTimer.current = setTimeout(connect, 5000);
        }
    }, []); // eslint-disable-line

    const handleMessage = (data) => {
        switch (data.type) {
            case 'log_new':
                setRealtimeLogs(prev => {
                    const updated = [data.log, ...prev].slice(0, MAX_LOGS);
                    return updated;
                });
                setNewLogCount(prev => prev + 1);
                if (listeners.current['log_new']) {
                    listeners.current['log_new'].forEach(fn => fn(data.log));
                }
                break;
            case 'log_updated':
                setRealtimeLogs(prev =>
                    prev.map(l => l.discordMessageId === data.log.discordMessageId ? data.log : l)
                );
                if (listeners.current['log_updated']) {
                    listeners.current['log_updated'].forEach(fn => fn(data.log));
                }
                break;
            case 'log_deleted':
                setRealtimeLogs(prev => prev.filter(l => l.discordMessageId !== data.logId));
                break;
            default:
                break;
        }
    };

    const subscribe = useCallback((event, fn) => {
        if (!listeners.current[event]) listeners.current[event] = new Set();
        listeners.current[event].add(fn);
        return () => listeners.current[event]?.delete(fn);
    }, []);

    const clearNewLogCount = useCallback(() => setNewLogCount(0), []);

    useEffect(() => {
        connect();
        return () => {
            if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
            wsRef.current?.close();
        };
    }, [connect]);

    return (
        <WebSocketContext.Provider value={{ connected, realtimeLogs, newLogCount, clearNewLogCount, subscribe }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export default WebSocketContext;
